import React, { FC, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { ExpandSmall } from '@agria/theme/src/components/icon';
import type { HeaderChildItems } from '../headers/header/Header';

interface NavDropdownProps {
  childItem: HeaderChildItems;
}

const StyledNavDropdown = styled.div`
  ${({ theme: { colors, space, layers, borderRadii } }) => css`
    button {
      svg {
        transition: rotate 0.2s ease;
      }

      &[aria-expanded='false'] svg {
        rotate: 180deg;
      }
    }

    ul {
      display: grid;
      position: absolute;
      width: 288px;
      background-color: ${colors.utilityWhite};
      padding: ${space.large} ${space.xxSmall} ${space.large} ${space.large};
      z-index: ${layers.headerSearch};
      border-bottom-left-radius: ${borderRadii.medium};
      border-bottom-right-radius: ${borderRadii.medium};
      box-shadow: 4px 16px 32px -8px rgba(3, 36, 62, 0.16);

      a {
        padding: ${space.small};
      }
    }

    button[aria-expanded='false'] + ul {
      display: none;
    }
  `};
`;

export const NavDropdown: FC<NavDropdownProps> = ({ childItem }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleChildNav = () => setIsOpen(!isOpen);

  const handleClickChildItem = () => {
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <StyledNavDropdown ref={dropdownRef}>
      <button type="button" onClick={toggleChildNav} aria-expanded={isOpen}>
        {childItem.link.label}
        <ExpandSmall aria-hidden="true" />
      </button>

      <ul>
        {childItem.navigationChildItems
          .filter((grandChildItem) => grandChildItem.link)
          .map((grandChildItem, grandChildIndex) => (
            <li key={grandChildIndex}>
              <LinkTo
                to={grandChildItem.link.url}
                onClick={handleClickChildItem}
              >
                {grandChildItem.link.label}
              </LinkTo>
            </li>
          ))}
      </ul>
    </StyledNavDropdown>
  );
};
